import { supabase } from './auth';

export async function getAccessibleClientIds(userId: string, userRole: string): Promise<string[]> {
  try {
    if (userRole === 'super_admin' || userRole === 'admin') {
      const { data: allClients, error } = await supabase
        .from('clients')
        .select('id');

      if (error) {
        console.error('Error fetching all clients:', error);
        return [];
      }

      return allClients?.map(c => c.id) || [];
    }

    const { data: clientLinks, error: linksError } = await supabase
      .from('profile_clients')
      .select('client_id')
      .eq('profile_id', userId);

    if (linksError) {
      console.warn('Error fetching profile_clients links, falling back to profiles.client_id:', linksError);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError || !profile?.client_id) {
        console.error('Error fetching profile client_id:', profileError);
        return [];
      }

      return [profile.client_id];
    }

    if (!clientLinks || clientLinks.length === 0) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError || !profile?.client_id) {
        return [];
      }

      return [profile.client_id];
    }

    return clientLinks.map(link => link.client_id);
  } catch (error) {
    console.error('Unexpected error in getAccessibleClientIds:', error);
    return [];
  }
}

export async function getUserAccessibleClients(userId: string, userRole: string) {
  try {
    const clientIds = await getAccessibleClientIds(userId, userRole);

    if (clientIds.length === 0) {
      return [];
    }

    const { data: clients, error } = await supabase
      .from('clients')
      .select('*')
      .in('id', clientIds)
      .order('statut', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching accessible clients:', error);
      return [];
    }

    return clients || [];
  } catch (error) {
    console.error('Unexpected error in getUserAccessibleClients:', error);
    return [];
  }
}

export async function addClientAssociation(profileId: string, clientId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('profile_clients')
      .insert({ profile_id: profileId, client_id: clientId });

    if (error) {
      console.error('Error adding client association:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error in addClientAssociation:', error);
    return false;
  }
}

export async function removeClientAssociation(profileId: string, clientId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('profile_clients')
      .delete()
      .eq('profile_id', profileId)
      .eq('client_id', clientId);

    if (error) {
      console.error('Error removing client association:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error in removeClientAssociation:', error);
    return false;
  }
}

export async function setUserClientAssociations(profileId: string, clientIds: string[]): Promise<boolean> {
  try {
    const { error: deleteError } = await supabase
      .from('profile_clients')
      .delete()
      .eq('profile_id', profileId);

    if (deleteError) {
      console.error('Error clearing existing associations:', deleteError);
      return false;
    }

    if (clientIds.length > 0) {
      const associations = clientIds.map(clientId => ({
        profile_id: profileId,
        client_id: clientId
      }));

      const { error: insertError } = await supabase
        .from('profile_clients')
        .insert(associations);

      if (insertError) {
        console.error('Error inserting new associations:', insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Unexpected error in setUserClientAssociations:', error);
    return false;
  }
}

export async function getUserClientAssociations(profileId: string): Promise<string[]> {
  try {
    const { data: links, error } = await supabase
      .from('profile_clients')
      .select('client_id')
      .eq('profile_id', profileId);

    if (error) {
      console.warn('Error fetching user client associations:', error);

      const { data: profile } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('user_id', profileId)
        .single();

      if (profile?.client_id) {
        return [profile.client_id];
      }

      return [];
    }

    return links?.map(l => l.client_id) || [];
  } catch (error) {
    console.error('Unexpected error in getUserClientAssociations:', error);
    return [];
  }
}
